import { PAGE_TYPE } from "@faire/deprecated-retailer-visitor-shared/consts/PAGE_TYPE";
import { getAltTag } from "@faire/deprecated-retailer-visitor-shared/features/Marketplace/utils";
import { ILeanProductTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { ProductTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/index";
import { ProductTileType } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { PrehydrationClickTrackingWrapper } from "@faire/deprecated-retailer-visitor-shared/lib/nextjs-rsc/PrehydrationClickTrackingWrapper";
import { useSearchPageIsRsc } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageIsRsc";
import { useSearchPageSpellCorrectedQuery } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/__internal__/useSearchPageSpellCorrectedQuery";
import { useSearchPageQuery } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/__internal__/LazySearchPage/__internal__/SearchPage/DataFetching/Products/__internal__/useSearchPageQuery";
import { useSearchPageIsFromDiscoverURL } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageIsFromDiscoverURL";
import { useSearchPageProductSearchString } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageProductSearchString";
import { getHelmetData } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/util/getHelmetData";
import { useSearchPageActionName } from "@faire/deprecated-retailer-visitor-shared/views/search-v2/util/useSearchPageActionName";
import { Surface } from "@faire/web-api--source/faire/logging/data/Surface";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { useStrictLocalization } from "@faire/web/common/localization";

import { useSearchTileInteractions } from "./utils";

export const LoggedOutSearchTileWrapper: React.FC<{
  productTile: ILeanProductTile;
  index?: number;
  brand?: ILeanProductTile.IBrand;
  requestId?: string;
  containerName?: string;
  productTileScreen: Surface;
  context: `${keyof typeof ProductTileContainer}|${string}`;
  productTileContainer: ProductTileContainer;
  contextToken: string;
  containerContext: ProductTileScreenContainer;
}> = ({
  productTile,
  index,
  brand,
  requestId,
  containerName,
  productTileScreen,
  context,
  productTileContainer,
  contextToken,
  containerContext,
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();

  const { tileActions, props } = useSearchTileInteractions(
    productTile,
    brand?.token
  );

  const productSearchString = useSearchPageProductSearchString();

  const spellCorrectedQuery = useSearchPageSpellCorrectedQuery();
  const { strictLocalize } = useStrictLocalization();
  const isFromDiscoverURL = useSearchPageIsFromDiscoverURL();
  const shouldShowVerticalImagery =
    useSearchPageQuery().data?.should_show_vertical_imagery ?? false;
  const finalQuery =
    spellCorrectedQuery === undefined
      ? productSearchString
      : spellCorrectedQuery;

  const { title } = getHelmetData(
    finalQuery,
    isFromDiscoverURL,
    strictLocalize
  );

  const templateAltTag = getAltTag(
    {
      pageType: PAGE_TYPE.DISCOVER,
      title,
      productName: productTile.product?.name,
    },
    strictLocalize
  );

  return (
    <SearchProductTilePrehydrationClickTrackingWrapper
      productToken={productTile.product?.token ?? ""}
    >
      <ProductTile
        key={index}
        brand={brand}
        product={productTile}
        type={ProductTileType.TILE_WITH_BRAND}
        isLoggedIn={isLoggedInRetailer}
        imageConfigProps={{
          altTag: templateAltTag,
          showVerticalImagery: shouldShowVerticalImagery,
        }}
        tileInteractions={{
          tileActions: tileActions,
          props: props,
        }}
        loggingContext={{
          impressionProviderProps: {
            context,
            productTileContainer,
            requestId,
            containerName,
            screenContext: productTileScreen,
            containerContext,
            contextToken,
            trackedLinkContainerName: containerName,
          },
        }}
      />
    </SearchProductTilePrehydrationClickTrackingWrapper>
  );
};

export const SearchProductTilePrehydrationClickTrackingWrapper = ({
  productToken,
  children,
}: {
  productToken: string;
  children: React.ReactNode;
}) => {
  const actionName = useSearchPageActionName();
  const isRsc = useSearchPageIsRsc();
  return (
    <PrehydrationClickTrackingWrapper
      name={actionName}
      isRsc={isRsc}
      location={`product-tile-${productToken}`}
    >
      {children}
    </PrehydrationClickTrackingWrapper>
  );
};
